import { db } from './config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getAuthHeaders } from './authHeaders'
import { getSellerIdsForUser } from './shopify';
import { getDocsWhereIn } from './queries';

// FedEx credentials used to be declared here. Nothing referenced them -- all
// tracking goes through the backend's EasyPost lookup -- and anything in this
// bundle is readable by every visitor, so they are gone. Treat the old client
// id, secret and account number as exposed and rotate them.

// Cloud Functions base URL
const CLOUD_FUNCTIONS_BASE_URL = 'https://api-yleo2sn4ca-uc.a.run.app';


/**
 * Looks up a shipment by tracking number.
 *
 * Despite the endpoint's name, this is EasyPost's tracker API, which detects
 * the carrier from the number itself -- UPS, USPS, FedEx, DHL and others all
 * work. The "FedEx" naming is historical and misleading; the backend route
 * keeps its old path so deployed clients don't break.
 */
export const trackShipment = async (trackingNumber) => {
    // try {
        console.log('🚀 Calling Firebase Cloud Function to track shipment...');
        console.log('Tracking number:', trackingNumber);
        
        // Create proper FedEx tracking payload
        const trackingPayload = {
            // includeDetailedScans: true,
            // trackingInfo: [
            //     {
            //         shipDateBegin: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
            //         shipDateEnd: new Date().toISOString().split('T')[0], // Today
            //         trackingNumberInfo: {
            //             trackingNumber: trackingNumber,
            //             carrierCode: "FDXE",
            //             trackingNumberUniqueId: null
            //         }
            //     }
            // ]
            tracking_code: trackingNumber
        };
        
        console.log('📦 Sending FedEx tracking payload:', JSON.stringify(trackingPayload, null, 2));
        
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/track-fedex-shipment`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(trackingPayload)
        });
        
        if (response.ok) {
            const result = await response.json();
            console.log('✅ REAL FedEx tracking data via Cloud Function:', result);
            
            // if (result.success && result.data) {
                // Add the public tracking URL and other info to the response
                const enhancedData = {
                    ...result,
                    publicTrackingUrl: result.public_url,
                    trackingNumber: result.tracking_code,
                    message: result.status
                };
                
                console.log('📋 Tracking message:', result.status);
                console.log('🔗 Public tracking URL:', result.public_url);
                
                return enhancedData;
            // } else {
            //     throw new Error(result.message || 'Failed to track shipment');
            // }
        } else {
            const errorData = await response.json();
            console.log('❌ Cloud Function failed:', errorData);
            throw new Error(errorData.message || 'Cloud Function failed');
        }
    // } catch (error) {
    //     console.error('Error tracking FedEx shipment via Cloud Function:', error);
        
    //     // Fallback to mock data for testing
    //     console.log('📊 Using mock tracking data until Cloud Function is deployed...');
    //     return {
    //         trackingNumber,
    //         status: 'delivered',
    //         estimatedDelivery: new Date().toISOString(),
    //         events: [
    //             {
    //                 timestamp: new Date().toISOString(),
    //                 eventType: 'DELIVERED',
    //                 eventDescription: 'Package delivered to recipient'
    //             }
    //         ]
    //     };
    // }
};








// Helper function to remove undefined values from objects
const removeUndefinedValues = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    
    if (Array.isArray(obj)) {
        return obj.map(removeUndefinedValues).filter(item => item !== null);
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            cleaned[key] = removeUndefinedValues(value);
        }
    }
    return cleaned;
};

// Create shipping record in Firestore
export const createShippingRecord = async (saleId, trackingNumber, sellerId, buyerId, labelData = null) => {
    try {
        // Clean the labelData to remove undefined values
        const cleanedLabelData = removeUndefinedValues(labelData);
        
        const shippingData = {
            sale_id: saleId,
            tracking_number: trackingNumber,
            easypost_id: null,
            carrier: null,
            seller_id: sellerId,
            buyer_id: buyerId,
            status: 'label_created',
            created_at: Date.now(),
            shipped_at: null,
            delivered_at: null,
            tracking_updates: [],
            
            // Basic label data (backward compatibility)
            label_url: cleanedLabelData?.labelUrl || null,
            pickup_scheduled: cleanedLabelData?.pickupScheduled || false,
            pickup_confirmation: cleanedLabelData?.pickupConfirmation || null,
            
            // Comprehensive FedEx data (only include if we have data)
            ...(cleanedLabelData && {
                fedex_data: {
                    // Tracking information
                    trackingNumber: cleanedLabelData.trackingNumber || trackingNumber,
                    serviceType: cleanedLabelData.serviceType || 'FEDEX_EXPRESS_SAVER',
                    serviceName: cleanedLabelData.serviceName || 'FedEx Express Saver®',
                    
                    // Dates (only include if they exist)
                    ...(cleanedLabelData.shipDate && { shipDate: cleanedLabelData.shipDate }),
                    ...(cleanedLabelData.estimatedDelivery && { estimatedDelivery: cleanedLabelData.estimatedDelivery }),
                    ...(cleanedLabelData.deliveryDay && { deliveryDay: cleanedLabelData.deliveryDay }),
                    ...(cleanedLabelData.deliveryTime && { deliveryTime: cleanedLabelData.deliveryTime }),
                    
                    // Package details
                    ...(cleanedLabelData.packageWeight !== undefined && { packageWeight: cleanedLabelData.packageWeight }),
                    ...(cleanedLabelData.packageDimensions && { packageDimensions: cleanedLabelData.packageDimensions }),
                    ...(cleanedLabelData.packageCount !== undefined && { packageCount: cleanedLabelData.packageCount }),
                    
                    // Shipping costs
                    ...(cleanedLabelData.shippingCost !== undefined && { shippingCost: cleanedLabelData.shippingCost }),
                    ...(cleanedLabelData.baseCharge !== undefined && { baseCharge: cleanedLabelData.baseCharge }),
                    
                    // Addresses (only include if they exist)
                    ...(cleanedLabelData.fromAddress && { fromAddress: cleanedLabelData.fromAddress }),
                    ...(cleanedLabelData.toAddress && { toAddress: cleanedLabelData.toAddress }),
                    
                    // Label and documents
                    ...(cleanedLabelData.labelUrl && { labelUrl: cleanedLabelData.labelUrl }),
                    labelFormat: cleanedLabelData.labelFormat || 'PDF',
                    
                    // FedEx specific data (only include if they exist)
                    ...(cleanedLabelData.fedExTransactionId && { fedExTransactionId: cleanedLabelData.fedExTransactionId }),
                    ...(cleanedLabelData.fedExJobId && { fedExJobId: cleanedLabelData.fedExJobId }),
                    fedExCarrierCode: cleanedLabelData.fedExCarrierCode || 'FDXE',
                    
                    // Pickup information
                    ...(cleanedLabelData.pickupScheduled !== undefined && { pickupScheduled: cleanedLabelData.pickupScheduled }),
                    ...(cleanedLabelData.pickupConfirmation && { pickupConfirmation: cleanedLabelData.pickupConfirmation }),
                    ...(cleanedLabelData.pickupDate && { pickupDate: cleanedLabelData.pickupDate }),
                    ...(cleanedLabelData.pickupTime && { pickupTime: cleanedLabelData.pickupTime })
                }
            })
        };

        console.log('📦 Saving shipping data to Firestore:', shippingData);
        const docRef = await addDoc(collection(db, 'shipping'), shippingData);
        return { id: docRef.id, ...shippingData };
    } catch (error) {
        console.error('Error creating shipping record:', error);
        throw error;
    }
};

// Update shipping status
export const updateShippingStatus = async (shippingId, status, trackingUpdates = []) => {
    try {
        const shippingRef = doc(db, 'shipping', shippingId);
        const updateData = {
            status,
            tracking_updates: trackingUpdates
        };

        if (status === 'shipped') {
            updateData.shipped_at = Date.now();
        } else if (status === 'delivered') {
            updateData.delivered_at = Date.now();
        }

        await updateDoc(shippingRef, updateData);
        return true;
    } catch (error) {
        console.error('Error updating shipping status:', error);
        throw error;
    }
};

// Get shipping records by user (as buyer or seller)
/**
 * Replaces the tracking number on an existing shipment.
 *
 * The carrier lookup is what moves a record from 'label_created' to
 * 'in_transit', and only then can the buyer confirm delivery. A mistyped or
 * unresolvable number therefore stranded the sale with no way out, since the
 * seller's input only appeared while no record existed.
 *
 * Status resets to 'label_created': the previous number's progress says
 * nothing about the new one, and re-tracking advances it again.
 */
export const updateShippingTrackingNumber = async (shippingId, trackingNumber) => {
    const shippingRef = doc(db, 'shipping', shippingId);
    const snapshot = await getDoc(shippingRef);

    if (!snapshot.exists()) {
        throw new Error('Shipping record not found');
    }
    if (snapshot.data().status === 'delivered') {
        throw new Error('This shipment is already delivered and cannot be changed');
    }

    const update = {
        tracking_number: trackingNumber,
        status: 'label_created',
        tracking_updates: [],
        tracking_number_updated_at: Date.now()
    };

    await updateDoc(shippingRef, update);
    return { id: snapshot.id, ...snapshot.data(), ...update };
};

export const getShippingRecordsByUser = async (userId) => {
    try {
        const shippingRef = collection(db, 'shipping');
        // Shipments for a Shopify sale are owned by the store, so match this
        // account's uid plus the role id of every store it owns.
        const sellerDocs = await getDocsWhereIn(
            shippingRef, 'seller_id', await getSellerIdsForUser(userId)
        );
        
        const q2 = query(
            shippingRef,
            where('buyer_id', '==', userId)
        );
        const buyerQuery = await getDocs(q2);

        const records = [];
        
        sellerDocs.forEach((doc) => {
            records.push({ id: doc.id, ...doc.data() });
        });
        
        buyerQuery.forEach((doc) => {
            records.push({ id: doc.id, ...doc.data() });
        });

        return records;
    } catch (error) {
        console.error('Error getting shipping records:', error);
        return [];
    }
};

// Get shipping record by sale ID
export const getShippingRecordBySaleId = async (saleId) => {
    try {
        const shippingRef = collection(db, 'shipping');
        const q = query(shippingRef, where('sale_id', '==', saleId));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return { id: doc.id, ...doc.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting shipping record by sale ID:', error);
        return null;
    }
};

// Complete delivery and release funds to seller
export const completeDelivery = async (shippingId, saleId) => {
    try {
        console.log('=== COMPLETING DELIVERY ===');
        console.log('Shipping ID:', shippingId);
        console.log('Sale ID:', saleId);

        // Update shipping status to delivered
        await updateShippingStatus(shippingId, 'delivered');
        
        // Get the sale record to update it
        const saleRef = doc(db, 'sales', saleId);
        const saleDoc = await getDoc(saleRef);
        
        if (saleDoc.exists()) {
            const saleData = saleDoc.data();

            if(saleData.status == "delivered"){
                throw new Error("Already set as delivered");
            }
            
            // // Update sale status
            // await updateDoc(saleRef, {
            //     status: 'delivered',
            //     delivered_at: Date.now()
            // });
            
            // Call Cloud Function to confirm delivery and update seller balance
            const response = await fetch('https://api-yleo2sn4ca-uc.a.run.app/confirm-delivery', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(await getAuthHeaders())
                },
                body: JSON.stringify({
                    cardId: saleData.cardId,
                    sellerId: saleData.receiver, // seller is the receiver in sales collection
                    buyerId: saleData.sender, // buyer is the sender in sales collection
                    price: saleData.price,
                    title: saleData.listing_title,
                    saleId: saleId,
                })
            });

            const result = await response.json();

            if (response.ok && result.success) {
                console.log('✅ Delivery completed successfully via Cloud Function');
                console.log('Seller earnings:', result.sellerEarnings);
                console.log('New balance:', result.newBalance);
                return true;
            } else {
                console.error('❌ Failed to complete delivery:', result.error);
                throw new Error(result.error || 'Failed to complete delivery');
            }
        }
        
        return false;
    } catch (error) {
        console.error('Error completing delivery:', error);
        throw error;
    }
}; 
